import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardMedia, Chip, Button, Stack, Pagination,
} from '@mui/material';
import { Add, Edit, Visibility, Bed, LocationOn } from '@mui/icons-material';
import { propertiesAPI } from '../../api/client';

function MyPropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await propertiesAPI.getMyProperties({ page, per_page: 12 });
        setProperties(res.data.properties || []);
        setTotal(res.data.total);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [page]);

  const statusColors = { active: 'success', inactive: 'default', under_review: 'warning', rejected: 'error', suspended: 'error' };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>My Properties</Typography>
        <Button component={Link} to="/dashboard/properties/create" variant="contained" startIcon={<Add />}>Add Property</Button>
      </Box>

      {properties.length === 0 && !loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No properties listed yet</Typography>
          <Button component={Link} to="/dashboard/properties/create" variant="contained" startIcon={<Add />} sx={{ mt: 2 }}>List Your First Property</Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {properties.map((prop) => {
            const img = prop.images?.find(i => i.is_primary) || prop.images?.[0];
            return (
              <Grid item xs={12} sm={6} md={4} key={prop.id}>
                <Card sx={{ height: '100%' }}>
                  <CardMedia sx={{ height: 160, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {img ? <Box component="img" src={img.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Bed sx={{ fontSize: 60, color: 'grey.400' }} />}
                  </CardMedia>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Chip label={prop.status.replace('_', ' ')} size="small" color={statusColors[prop.status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                      <Typography variant="body2" color="text.secondary">{prop.views_count} views</Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight={600} noWrap>{prop.title}</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                      <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" noWrap>{prop.city}, {prop.region}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{prop.available_rooms}/{prop.total_rooms} rooms available</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary" sx={{ mt: 1 }}>
                      {prop.currency} {(prop.price_per_month || prop.price_per_semester || 0).toLocaleString()}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Button size="small" variant="outlined" startIcon={<Edit />} component={Link} to={`/dashboard/properties/${prop.id}/edit`}>Edit</Button>
                      <Button size="small" variant="text" startIcon={<Visibility />} component={Link} to={`/properties/${prop.id}`}>View</Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      {total > 12 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination count={Math.ceil(total / 12)} page={page} onChange={(e, v) => setPage(v)} color="primary" />
        </Box>
      )}
    </Box>
  );
}

export default MyPropertiesPage;
